import { CircleCheckBig, ListTodo, Redo, TicketSlash } from "lucide-react";

import type { UsersTicketsHistorySchema } from "@upclaim/sdk/src/data-contracts";

import { copyToClipboard } from "@/lib/clipboard";

type UserTicketHistory = UsersTicketsHistorySchema[number];
export type LegHistory = UserTicketHistory["tickets"][number]["legs"][number];

/** Port of reservation/TicketStatusBadge.vue. */
export function TicketStatusBadge({
  status,
  statusDescription,
}: {
  status?: string | null;
  statusDescription?: string | null;
}) {
  if (status === "B") {
    return (
      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">
        <CircleCheckBig size={14} strokeWidth={2.5} className="mr-1" />
        Vol effectué
      </span>
    );
  }
  if (status === "I") {
    return (
      <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
        Émission d'origine
      </span>
    );
  }
  if (status === "E") {
    return (
      <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-700/10 ring-inset">
        <TicketSlash size={14} strokeWidth={2.5} className="mr-1" />
        Ticket échangé
      </span>
    );
  }
  if (status === "AL") {
    return (
      <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset">
        {statusDescription}
      </span>
    );
  }
  if (status === "CK") {
    return (
      <span className="inline-flex items-center rounded-md bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700 ring-1 ring-pink-700/10 ring-inset">
        <ListTodo size={14} strokeWidth={2.5} className="mr-1" />
        Check-in
      </span>
    );
  }
  if (status === "RF") {
    return (
      <span className="inline-flex items-center rounded-md bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700 ring-1 ring-pink-700/10 ring-inset">
        <Redo size={14} strokeWidth={2.5} className="mr-1" />
        Ticket remboursé
      </span>
    );
  }
  if (status) {
    return (
      <span className="inline-flex items-center rounded-md bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700 ring-1 ring-pink-700/10 ring-inset">
        {statusDescription}
      </span>
    );
  }
  return null;
}

/** Port of reservation/InvoluntaryBadge.vue. */
export function InvoluntaryBadge({ involuntaryIndicator }: { involuntaryIndicator?: string | null }) {
  if (!involuntaryIndicator) {
    return null;
  }
  return (
    <span className="my-1 inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-gray-200 ring-inset">
      <span className="flex-none rounded-full bg-red-600/20 p-1">
        <span className="block h-1.5 w-1.5 rounded-full bg-red-600" />
      </span>
      {involuntaryIndicator === "Involuntary"
        ? "Involontaire"
        : involuntaryIndicator === "Cancellation of flight"
          ? "Annulation du vol"
          : involuntaryIndicator}
    </span>
  );
}

/** Port of reservation/CouponHistoryDetail.vue — coupon versions, most recent first. */
export function CouponHistoryDetail({ legHistory }: { legHistory: LegHistory }) {
  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="text-left sm:flex-auto">
          <h1 className="text-base leading-6 font-semibold text-gray-900">Historique du Coupon</h1>
          <p className="mt-2 text-sm text-gray-700">De la version la plus récente à la plus ancienne.</p>
        </div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Vol
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Départ
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Arrivée
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Statut
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Involuntary Indicator?
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Classe
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Date d'émission
                    </th>
                    <th scope="col" className="relative py-3.5 pr-4 pl-3 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {legHistory.map((leg) => (
                    <tr key={leg.itemNumber}>
                      <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-6">
                        <a
                          href={`https://fr.flightaware.com/live/flight/${leg.flight.flightIdentifier}/history`}
                          target="_blank"
                          rel="noreferrer"
                          className="underline hover:text-indigo-600"
                        >
                          {leg.flight.flightIdentifier}
                        </a>
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-700">
                        {leg.localDepartureString?.split(" UTC")[0]}
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-700">
                        {leg.localArrivalString?.split(" UTC")[0]}
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                        <TicketStatusBadge status={leg.status} statusDescription={leg.statusDescription} />
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                        <InvoluntaryBadge involuntaryIndicator={leg.involuntaryIndicator} />
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                        {leg.bookingClass}
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                        {leg.issueDate ? new Date(leg.issueDate).toLocaleDateString() : ""}
                      </td>
                      <td className="relative py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-6">
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={() => void copyToClipboard(leg.flightId)}
                        >
                          ID du vol
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
